// Money formatting, in two halves.
//
// DISPLAY -- turning a number into text for the screen or a PDF. Ten files each
// grew their own helper and they disagreed: an empty value rendered as an em dash,
// a hyphen or 0.00; negatives appeared as (1,234) in the statements and -1,234
// elsewhere; some carried the RM prefix and some left it to the caller. The
// variants below are the ones the app genuinely needs and nothing else. Each takes
// the value first and the empty-case rendering second, so a caller needing `null`
// or '0.00' instead of a dash says so rather than defining a new helper.
//
// INPUT -- keeping a money text field readable while it is being typed. Further
// down, unchanged.

export const EM_DASH = '—';

const grouped = (n, digits) =>
  Number(n).toLocaleString('en-MY', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// null, undefined, '' and NaN all count as "no value". A numeric 0 does NOT --
// zero revenue is a fact worth showing, not a blank.
const isEmpty = (v) => v == null || v === '' || isNaN(Number(v));

// 'RM 1,234.50' -- the default for an amount on screen.
export function money(v, empty = EM_DASH) {
  return isEmpty(v) ? empty : 'RM ' + grouped(v, 2);
}

// 'RM 1,235' -- whole ringgit, where decimals are noise (scoreboards, targets).
export function money0(v, empty = EM_DASH) {
  return isEmpty(v) ? empty : 'RM ' + grouped(v, 0);
}

// '1,234.50' / '(1,234.50)' -- accounting style: no prefix, negatives bracketed.
export function accounting(v, empty = EM_DASH) {
  if (isEmpty(v)) return empty;
  const n = Number(v);
  const s = grouped(Math.abs(n), 2);
  return n < 0 ? '(' + s + ')' : s;
}

// 'RM 1,234.50' / '(RM 1,234.50)' -- accounting with the prefix inside the brackets.
export function accountingRM(v, empty = EM_DASH) {
  if (isEmpty(v)) return empty;
  const n = Number(v);
  const s = grouped(Math.abs(n), 2);
  return n < 0 ? `(RM ${s})` : `RM ${s}`;
}

// '1,235' / '(1,235)' -- accounting, rounded to whole ringgit.
export function accounting0(v, empty = EM_DASH) {
  if (isEmpty(v)) return empty;
  const n = Math.round(Number(v));
  const s = grouped(Math.abs(n), 0);
  return n < 0 ? '(' + s + ')' : s;
}

// '187k' -- for the narrow rail, where the full figure will not fit.
export function compactK(v, empty = EM_DASH) {
  return isEmpty(v) ? empty : `${Math.round(Number(v) / 1000)}k`;
}

// ---------------------------------------------------------------------------
// INPUT: money field formatting, shared by the JQ and Zen booking forms.
// ---------------------------------------------------------------------------

// "250,000.50" -> "250000.50". Always run this before parseFloat: parseFloat
// stops at the first comma, so parseFloat("250,000") is 250, not 250000.
export const stripThousands = (value) => String(value ?? '').replace(/,/g, '');

// "250000.5" -> "250,000.5". Groups the integer part only, and leaves a trailing
// "." alone so typing "250000." doesn't fight the user mid-keystroke.
export const formatThousands = (value) => {
  const raw = stripThousands(value);
  if (raw === '') return '';
  const [int, ...rest] = raw.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return rest.length ? `${grouped}.${rest.join('.')}` : grouped;
};

// Keep only digits and a single decimal point, then regroup. Anything else the
// user types (letters, a second dot, a stray comma) is simply dropped.
export const sanitizeMoneyInput = (value) => {
  const cleaned = stripThousands(value).replace(/[^\d.]/g, '');
  const [int, ...rest] = cleaned.split('.');
  const normalized = rest.length ? `${int}.${rest.join('')}` : int;
  return formatThousands(normalized);
};

// Regrouping the digits on every keystroke moves the caret: inserting a comma to
// the left of the cursor pushes the cursor a character further left than the user
// left it, so typing "250000" lands as "205,000". This counts non-comma characters
// before the caret, then re-finds that same position in the regrouped string.
//
// Pass the change event and a setter; returns nothing. Used as
//   onChange={(e) => handleMoneyInput(e, (v) => setForm({ ...form, amount: v }))}
export const handleMoneyInput = (e, setValue) => {
  const el = e.target;
  const caret = el.selectionStart ?? el.value.length;
  const charsBeforeCaret = stripThousands(el.value.slice(0, caret)).length;
  const next = sanitizeMoneyInput(el.value);
  setValue(next);

  requestAnimationFrame(() => {
    let seen = 0;
    let pos = 0;
    while (pos < next.length && seen < charsBeforeCaret) {
      if (next[pos] !== ',') seen += 1;
      pos += 1;
    }
    try { el.setSelectionRange(pos, pos); } catch { /* field already blurred */ }
  });
};
